/**
 * exercise-edit.js
 */

import React from 'react';
import axios from 'axios';
import {
  IconButton,
  useMediaQuery,
} from '@mui/material';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import { useTheme } from '@mui/material/styles';
import { useWatch, useForm, FormProvider } from 'react-hook-form';
import { none } from '@hookstate/core'
import { useSnackbar } from 'notistack';
import { Alert } from '@mui/material';
import { TOUR_BEACON_VIEW_LIMIT, Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { shouldRenderMobileLayout } from 'utils/app-context';
import useMounted from 'utils/use-mounted';
import analytics from 'utils/analytics';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import { DogsContext } from 'contexts/dogs/dogs-context';
import { TourContext } from 'contexts/tour/tour-context';
import DrawerContent from 'components/menu-drawer/drawer-content';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import TrainingWizardStepper from 'components/training/training-drawer/training-wizard-stepper';
import Button from 'components/button';
import Icon from 'components/icon';
import HiddenInput from 'components/form-inputs/hidden-input';
import useTours from 'components/tour/tour-data';
import { DetectionOdorsContext } from 'components/detection-odors/context/detection-odors-context';
import ExerciseViewBase from '../view/exercise-view-base';
import ExerciseEditMain from './exercise-edit-main';
import ExerciseEditApplicableDogs from './exercise-edit-applicable-dogs';
import ExerciseEditDetection from './exercise-edit-detection';
import ExerciseEditPatrol from './exercise-edit-patrol';
import ExerciseEditGoal from './exercise-edit-goal';


// use wrapper to remount component when exercise ID changes
export default function ExerciseEdit(props) {
  const params = useParams();
  const exerciseId = params.exerciseId ? parseInt(params.exerciseId) : 0;

  return <ExerciseEditImplementation 
    exerciseId={exerciseId}
    key={exerciseId} 
    {...props} 
  />;
}


function ExerciseEditImplementation(props) {
  const {
    exerciseId,
    setDesktopActions,
    setMobileAppBar,
    onClose,
    wizard,
    basePath,
    drawerState,
  } = props;
  const theme = useTheme();
  let history = useHistory();
  const path = history.location.pathname;
  const translation = 'exerciseEdit';
  const { user } = React.useContext(UserContext);
  const { dogs } = React.useContext(DogsContext);
  const { activeTour, toursSeen, beaconViews } = React.useContext(TourContext);
  const { currentDoneHandler } = React.useContext(DetectionOdorsContext);
  const isMounted = useMounted();
  const { enqueueSnackbar } = useSnackbar();
  const recordStore = useRecordStore();
  const params = useParams();
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId);
  const eventId = params.eventId
    ? parseInt(params.eventId)
    : exerciseRecord.get()
      ? exerciseRecord.get().eventId
      : null;
  const exerciseName = exerciseRecord.get() ? exerciseRecord.get().name : '';
  const [storedRecord, setStoredRecord] = React.useState();
  const [options, setOptions] = React.useState();
  const [submittedState, setSubmittedState] = React.useState();
  const { startTour } = useTours();
  const isSizeXS = useMediaQuery(theme.breakpoints.down('sm'));

  // hook form
  const formContext = useForm();
  const { control, formState, handleSubmit, reset } = formContext;
  const dirtyFields = formState.dirtyFields;
  const isDirty = Object.keys(dirtyFields).length !== 0;
  const workModeValue = useWatch({ control, name: 'workMode' })

  // props sent to any sub-form component
  const formProps = {
    exerciseRecord,
    storedRecord,
    exerciseId,
    drawerState,
    options,
    translation,
    workModeValue,  // TODO: dedup in child components
  };


  // check if we should start a product tour
  React.useEffect(() => {
    const isTourConditionsValid = activeTour < 0 && !isSizeXS && user !== null && dogs.length > 0;
    const isTourPathValid = path.includes("wz/2") || path.includes("/edit/exercise") || path.includes("/add-exercise");
    const isFormLoaded = storedRecord !== undefined;

    if (isTourConditionsValid && isTourPathValid && isFormLoaded) {
      const editorBeaconCount = beaconViews[2] ?? 0;
      const templateBeaconCount = beaconViews[3] ?? 0;
      const dogBeaconCount = beaconViews[4] ?? 0;
      if (!toursSeen.includes(2) && user.completeExercisesCount >= 0 && editorBeaconCount <= TOUR_BEACON_VIEW_LIMIT) {
        setTimeout(() => startTour(2), 1000); // Exercise editor tour
      }
      else if (!toursSeen.includes(3) && user.completeExercisesCount >= 2 && templateBeaconCount <= TOUR_BEACON_VIEW_LIMIT) {
        setTimeout(() => startTour(3), 1000); // Exercise templates tour
      }
      else if (!toursSeen.includes(4) && user.completeExercisesCount >= 5 && dogBeaconCount <= TOUR_BEACON_VIEW_LIMIT) {
        setTimeout(() => startTour(4), 1000); // Exercise dog applicability tour
      }
    }
  }, [path, isSizeXS, user, dogs, activeTour, toursSeen, beaconViews, storedRecord, startTour]);


  // get exercise options on init
  React.useEffect(() => {
    setTimeout(() => drawerState.loading.content.set(true));
    (async () => {
      try {
        if (parseInt(exerciseId) >= 0 && parseInt(eventId) >= 0) {
          const response = await axios.get(`/training/exercise/${exerciseId}/${eventId}/edit`);

          if (!isMounted.current) return;
          if (response.status !== 200) return;

          const editData = response.data;
          const trainingExer = JSON.parse(JSON.stringify(recordStore.updateRecord(editData.exercise).get()));
          // Remove answers from form data. With react-hook-form v7 these get stored and 
          // included with submission data even though there is no corresponding form field,
          // causing a submission error. Using shouldUnregister in useForm prevents this
          // but causes other problems.
          const { answers: _, ...formInitData } = trainingExer;
          reset({ ...formInitData, eventId}, { keepValues: false });
          setStoredRecord(trainingExer);
          setOptions(editData.options);

          drawerState.loading.content.set(false);
        }

      } catch (e) {
        if (e.response && e.response.status === 404) {
          return history.push(basePath);
        }
        if (e.response && e.response.status === 403) {
          return history.push(makeLocation(
            basePath,
            'training',
            'viewExercise',
            {exerciseId, eventId},
            history.location
          ));
        }
      }
    })();

    // reset 0 state on closing so we don't have bad cached data later
    return () => {
      if (exerciseId === 0) {
        exerciseRecord.set(none);
      }
    }

  // recordStore & drawerState & exerciseRecord are hookstates
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId, eventId, basePath, history, isMounted, reset]);


  const getHandlersWithCompletions = () => {
    return storedRecord.applicableHandlerDogs
      && storedRecord.applicableHandlerDogs
        .filter(h => h.hasCompletions)
        .map(h => h.id === user.id ? 'Me' : h.name).join(', ');
  }


  const restoreOriginal = () => {
    if (storedRecord) {
      exerciseRecord.set(storedRecord);
    }
  };


  // Ensure that ConfirmUnsavedEditor props are up to date by triggering 
  // a re-render before proceding with save and navigation.
  // (onSubmit > useEffect > afterSubmit)
  const onSubmit = React.useCallback((data, noNav) => {
    setSubmittedState({ data, noNav });
  }, []);


  const afterSubmit = React.useCallback(async (data, noNav) => {
    drawerState.loading.content.set(true);

    if (exerciseId !== 0) {
      // get prev exercise render in case it's needed for completion invalidation support
      data.previousRender = document.getElementById('previous-render').innerText;
    }

    try {
      const { record: savedExerciseRecord } = await recordStore.saveExercise(exerciseId, data);
      if (savedExerciseRecord.get()) {
        if (exerciseId === 0) {
          // track which predicted fields were changed (workMode, patrolType, none)
          let dirtyPredictedFields = [];
          if (dirtyFields.workMode) dirtyPredictedFields.push('WorkMode');
          if (!dirtyFields.detection && dirtyFields.patrol?.patrolTypes?.[0]?.type) dirtyPredictedFields.push('PatrolType');
          const fields = dirtyPredictedFields.length > 0 ? dirtyPredictedFields.join(',') : 'None';
          analytics.trackGA4('event', { name: 'exercise_predictionfix', params: { modified_fields: fields } });
        } 

        if (wizard) {
          if (noNav !== true) {
            // wizard exercise save
            wizard.onSave(savedExerciseRecord.get(), history, basePath, recordStore, user);
          }
        } else {
          // regular exercise save
          enqueueSnackbar('Exercise saved', { variant: 'success' });
          if (noNav !== true) {
            history.push(makeLocation(
              basePath,
              'training',
              'viewExercise',
              {exerciseId: savedExerciseRecord.get().id},
              history.location
            ));
          }
        }
      }
    } catch (e) {
      enqueueSnackbar('Error saving exercise', {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });

      drawerState.loading.content.set(false);
    }

  // recordStore & drawerState cause infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId, basePath, enqueueSnackbar, history, wizard, dirtyFields, user]);


  React.useEffect(() => {
    if (submittedState) {
      afterSubmit(submittedState.data, submittedState.noNav);
    }
    // only call afterSubmit when submittedState changes, not when afterSubmit itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedState]);


  const onSave = React.useCallback(async () => {
    if (currentDoneHandler && !(await currentDoneHandler())) {
      return;
    }
    handleSubmit(onSubmit)();
  }, [currentDoneHandler, handleSubmit, onSubmit]);


  const onSkip = React.useCallback(() => {
    wizard.onSave(storedRecord, history, basePath, recordStore, user);

  // recordStore causes infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard, storedRecord, history, basePath, user]);


  const onCancel = React.useCallback(() => {
    if (exerciseId === 0) {
      history.goBack();
    } else {
      history.push(makeLocation(
        basePath,
        'training',
        'viewExercise',
        {exerciseId},
        history.location
      ));
    }
  }, [history, basePath, exerciseId]);


  const cancelButton = React.useMemo(() => (
    <DrawerActionButton
      onClick={onCancel}
    >
      Cancel
    </DrawerActionButton>
  ), [onCancel]);


  const saveButton = React.useMemo(() => (
    <DrawerActionButton
      level={1}
      disabled={!isDirty && exerciseId !== 0 && !wizard}
      onClick={onSave}
      style={{ marginLeft: 16 }}
      data-testid="save-button"
    >
      {wizard ? 'Next' : 'Save'}
    </DrawerActionButton>
  ), [wizard, onSave, isDirty, exerciseId]);


  const skipButton = React.useMemo(() => (
    <DrawerActionButton
      onClick={onSkip}
      data-testid="skip-button"
    >
      Skip
    </DrawerActionButton>
  ), [onSkip]);


  const closeButton = React.useMemo(() => (
    <IconButton
      data-testid="mobile-drawer-close-button"
      onClick={onClose}
      size="small"
      style={{color: 'inherit'}}
    >
      <Icon type='close' />
    </IconButton>
  ), [onClose]);


  React.useEffect(() => {
    if (setDesktopActions) {
      setDesktopActions(wizard
        ? <>{skipButton}{saveButton}</>
        : <>{cancelButton}{saveButton}</>);
    }
    if (setMobileAppBar) {
      setMobileAppBar({
        title: 'Exercise',
        leftActions: exerciseId === 0 ? closeButton : cancelButton,
        rightActions: saveButton,
        recordGroupType: Types.RecordGroup.TRAINING,
      });
    }
  }, [
    setDesktopActions, 
    setMobileAppBar,
    exerciseId,
    wizard,
    cancelButton,
    saveButton,
    skipButton,
    closeButton
  ]);


  return (
    <>
      {storedRecord && options &&
      <FormProvider {...formContext}>
        <form onSubmit={handleSubmit(onSubmit)}>

          {/* mobile sub title */}
          <MobilePageTitle
            title={exerciseName}
            rightAction={wizard &&
              <Button
                upperCase
                endIcon={<Icon type='arrow-right' />}
                onClick={onSkip}
                children='skip step'
              />}
          />

          <DrawerContent>

            {shouldRenderMobileLayout() && wizard &&
            <TrainingWizardStepper basePath={basePath} />}

            {/* event id - hidden input */}
            <HiddenInput name='eventId' />

            {/* has completions warning */}
            {storedRecord.fullStats?.complete > 0 &&
            storedRecord.applicableHandlerDogs.length > 1 &&
            <Alert severity="warning" style={{ marginBottom: 24 }}>
              <>
                One or more handlers have already completed this exercise. 
                If you make modifications to the exercise definition then the handlers will be notified, asked to review the changes and then make any adjustments to their records. 
                Note that some aspects of the exercise such as the type can't be changed until all completion records are removed.{' '}
              </>
              <>
                Handlers who have completed this exercise: {getHandlersWithCompletions()}.
              </>
            </Alert>}

            {/* main section */}
            <ExerciseEditMain {...formProps} />

            {/* detection environments & odors */}
            <ExerciseEditDetection {...formProps} />

            {/* patrol exercise types */}
            <ExerciseEditPatrol {...formProps} />

            {/* goal / description */}
            <ExerciseEditGoal {...formProps} />

            {/* dog applicability */}
            <ExerciseEditApplicableDogs {...formProps} />

            <div id='previous-render' style={{ height: 1, overflow: 'hidden'}}>
              <ExerciseViewBase exerciseRecord={storedRecord} showName />
            </div>

          </DrawerContent>

        </form>
      </FormProvider>}     

      <ConfirmUnsavedEditor
        dirty={isDirty && !formState.isSubmitted}
        discard={restoreOriginal}
        save={async () => {
          return await new Promise((resolve) => {
            handleSubmit(
              async (data) => {
                onSubmit(data, true);
                return resolve(true);
              },
              () => {
                return resolve(false);
              })();
          });
        }}
      />
    </>
  );
}